import React, { useState, useEffect } from 'react';
import { generateDisplayName } from '@/shared/utils/displayName';
import { supabase } from '@/supabaseClient';
import * as Sentry from '@sentry/browser';

export function DisplayNameSetup({ user, onComplete }) {
  const [displayName, setDisplayName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(true);

  // Generate default display name when component mounts
  useEffect(() => {
    if (user?.email) {
      const defaultName = generateDisplayName(user.email);
      setDisplayName(defaultName);
      console.log('Generated default display name:', defaultName);
    }
  }, [user?.email]);

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }

    console.log('Saving display name for new user:', displayName);
    setIsSaving(true);
    setError('');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const updatedProfile = await response.json();
      console.log('Successfully saved display name for new user:', updatedProfile);
      
      setIsVisible(false);
      setTimeout(() => {
        onComplete?.(updatedProfile);
      }, 200); // Small delay for smooth transition
    } catch (error) {
      console.error('Error saving display name:', error);
      Sentry.captureException(error, {
        extra: { 
          userEmail: user?.email,
          displayName: displayName.trim(),
          context: 'new-user-display-name-setup'
        }
      });
      setError(error.message || 'Failed to save display name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-violet-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
              {displayName ? displayName.charAt(0).toUpperCase() : '?'}
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Welcome to Zaptopia!
            </h2>
            <p className="text-slate-600">
              Please set up your display name so others can recognize you in the community.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-slate-700 mb-2">
                Your Display Name *
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-slate-800"
                placeholder="Enter your display name"
                maxLength={50}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                This is how your name will appear to other users. You can change this later in your account settings.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSaving || !displayName.trim()}
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                    Setting up...
                  </>
                ) : (
                  'Confirm Display Name'
                )}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="text-center mt-6 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              Powered by{' '}
              <a 
                href="https://www.zapt.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                ZAPT
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}